<!-- TableRow.svelte -->
<script lang="ts">
  import { cn } from "$lib/utils";
  import type { ColumnDefinition } from "./types";
  import type { SearchResult } from "$lib/types";
  import { Checkbox } from "$lib/components/ui/checkbox";
  import { ProfilePicture, getImageUrl } from "$lib/components/basic/profilePicture";
  import { simpleHash } from "$lib/utility";
  import { Button } from "$lib/components/ui/button";
  import { Mail } from "lucide-svelte";
  import EmailCopyDialog from "$lib/components/dialogs/copyEmailDialog.svelte";
  import { authStore as Current } from "$lib/stores";
  import { page } from "$app/state";
  import { searchStore } from "$lib/stores";
  import CriteriaHover from "./criteriaHover.svelte";

  let {
    result,
    rowIndex,
    selectedCell,
    fixedColumns,
    dynamicColumns,
    columnConfigs,
    currentSearchQuery = "",
    type = "person",
    selectedResults,
    isViewedByOwner = false,
    loggedOutCallback = () => {},
    upsellCallback = () => {},
    onSelectRow,
    onRowClick,
    onCellClick,
  } = $props<{
    result: SearchResult;
    rowIndex: number;
    selectedCell: { row: number | null; col: number | null };
    fixedColumns: ColumnDefinition[];
    dynamicColumns: ColumnDefinition[];
    columnConfigs: Record<string, ColumnDefinition>;
    currentSearchQuery?: string;
    type?: "person" | "company";
    selectedResults: Set<string>;
    isViewedByOwner?: boolean;
    loggedOutCallback?: () => void;
    upsellCallback?: () => void;
    onSelectRow: (id: string) => void;
    onRowClick: (e: MouseEvent, result: SearchResult, index: number) => void;
    onCellClick: (e: MouseEvent, result: SearchResult, rowIndex: number, cellIndex: number) => void;
  }>();

  let isCurrentRow = $derived(selectedCell.row === rowIndex);

  function highlightText(text: string, query: string): string {
    if (!query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    return text.replace(regex, '<mark class="bg-theme-green leading-tight dark:bg-yellow-800">$1</mark>');
  }
</script>

<tr
  class={cn(
    "group border-b border-theme-text-tertiary transition-colors dark:border-white/10 ",
    isCurrentRow ? "bg-theme-background-primary dark:bg-theme-background-primary" : "hover:bg-theme-background-primary",
    "cursor-pointer",
  )}
>
  <!-- Fixed columns -->
  {#each fixedColumns as column, index}
    <td
      class={cn(
        "border-b border-r border-theme-text-tertiary dark:border-white/10",
        "bg-inherit",
        column.sticky ? `sticky left-${index * 12} z-20` : "",
        `text-${column.align}`,
        "box-border",
        "whitespace-nowrap",
        isCurrentRow
          ? "bg-theme-background-primary dark:bg-theme-background-primary"
          : "bg-gray-50 bg-theme-background-primary group-hover:bg-gray-50 dark:bg-theme-background-primary dark:group-hover:bg-theme-background-primary  dark:group-hover:text-theme-green",
        column.key == "index" && isCurrentRow ? "bg-theme-background-secondary" : "",
        // `w-[${column.width}px]`, `min-w-[${column.minWidth ?? column.width}px]`
      )}
      style=""
    >
      {#if column.key === "index"}
        <span class="px-4 text-2xs">{rowIndex + 1}</span>
      {:else}
        <div class="px-3">
          <Checkbox
            checked={selectedResults.has(result.id)}
            onCheckedChange={() => onSelectRow(result.id)}
            class="!rounded-base"
            data-interactive
          />
        </div>
      {/if}
    </td>
  {/each}

  <!-- Dynamic columns -->
  {#each dynamicColumns as column, colIndex}
    <td
      class={cn(
        "border-r border-theme-text-tertiary px-4 py-3 dark:border-white/10",
        column.className,
        `text-${column.align}`,
        "box-border",
        "text-theme-text-secondary",
        selectedCell.row === rowIndex && selectedCell.col === colIndex
          ? "border-2 border-black dark:border-theme-green dark:bg-theme-background-secondary"
          : "",
      )}
      style="width: {columnConfigs[column.key].width}px;
            min-width: {columnConfigs[column.key].minWidth ?? columnConfigs[column.key].width}px;"
      data-row={rowIndex}
      data-col={colIndex}
      onclick={(e) => onCellClick(e, result, rowIndex, colIndex)}
    >
      {#if column.key === "criteria"}
        <!-- <div class="flex flex-col gap-1">
          {#each result.criteriaAndReasons?.slice(0, 2) ?? [] as cr}
            <div class="flex items-center gap-1">
              <span class={cn("text-xs font-medium", cr.isMatch === "match" ? "text-green-600" : "text-red-600")}>
                {cr.isMatch === "match" ? "✓" : "✗"}
              </span>
              <span
                class={cn(
                  "max-w-[200px] truncate text-xs text-gray-600 dark:text-gray-400",
                  selectedCell.row === rowIndex && selectedCell.col === colIndex ? "" : "",
                )}
              >
                {(() => {
                  try {
                    return JSON.parse(cr.criteria).displayShort.replace(/\.$/, "");
                  } catch {
                    return cr.criteria.replace(/\.$/, "");
                  }
                })()}
              </span>
            </div>
          {/each}
          {#if (result.criteriaAndReasons?.length ?? 0) > 2}
            <span
              class={cn(
                "text-xs text-gray-500",
                selectedCell.row === rowIndex && selectedCell.col === colIndex ? "" : "",
              )}
            >
              +{result.criteriaAndReasons!.length - 2} more
            </span>
          {/if}
        </div> -->
        <CriteriaHover
          {result}
          {rowIndex}
          {selectedCell}
          {colIndex}
          onClick={(e) => {
            onCellClick(e, result, rowIndex, colIndex);
          }}
        />
      {:else if column.key === "name"}
        <div class="flex items-center gap-3">
          <ProfilePicture
            wrapperClass={cn(
              "min-w-9 min-h-9 max-w-9 max-h-9 p-0.5",
              selectedCell.row === rowIndex && selectedCell.col === colIndex
                ? "bg-theme-green dark:bg-theme-background-secondary border-black"
                : "",
            )}
            imageUrl={getImageUrl(result)}
            fallbackImageUrl="https://www.gravatar.com/avatar/default?d=identicon&s=256"
            shape={type === "person" ? "circle" : "square"}
            outline={true}
            altText={type === "person" ? "Profile" : "Company Logo"}
          />
          <div class="flex flex-col text-xs font-normal text-gray-600 dark:text-gray-400">
            <span class="">
              {@html highlightText(
                type === "person" ? `${result.firstName} ${result.lastName}` : result.companyName,
                currentSearchQuery,
              )}
            </span>
            {#if type !== "person" && result.domain}
              <span class="">
                {result.domain}
              </span>
            {/if}
          </div>
        </div>
      {:else if column.key === "contact" && type === "person"}
        <div class="flex items-center gap-2">
          {#if result.email}
            <Button
              class="flex h-fit w-fit gap-1.5 !rounded-base px-0 py-0 text-2xs font-normal text-gray-600 dark:text-gray-400"
              variant="ghost"
              onclick={async (e: Event) => {
                try {
                  await navigator.clipboard.writeText(result.email);
                  const button = document.activeElement as HTMLButtonElement;
                  const originalContent = button.innerHTML;
                  button.innerHTML = `<svg class="text-theme-green-deep" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>Copied!`;
                  setTimeout(() => {
                    button.innerHTML = originalContent;
                  }, 2000);
                } catch (error) {
                  console.error("Failed to copy:", error);
                }
              }}
            >
              <Mail strokeWidth={1} class="max-h-3" />
              {result.email}
            </Button>
          {:else if isViewedByOwner}
            <EmailCopyDialog
              entityId={result.id}
              {type}
              displayName={`${result.firstName} ${result.lastName}`}
              on:emailUpdate={(e) => {
                searchStore.updateSearchResult(
                  page.params.slug,
                  result.id,
                  { email: e.detail.email },
                  { skipRefresh: true },
                );
                Current.updateUserCredits();
              }}
            >
              <Button
                variant="ghost"
                class="flex h-fit w-fit gap-1.5 !rounded-base px-0 py-0 text-2xs font-normal text-gray-600 dark:text-gray-400"
              >
                <Mail strokeWidth={1} class="max-h-3" />
                [show email]
              </Button>
            </EmailCopyDialog>
          {:else}
            <Button
              class="flex h-fit w-fit gap-1.5 !rounded-base px-0 py-0 text-2xs font-normal text-gray-600 dark:text-gray-400"
              variant="ghost"
              onclick={upsellCallback}
            >
              <Mail strokeWidth={1} class="max-h-3" />
              view email
            </Button>
          {/if}
        </div>
      {:else if column.key === "title"}
        <span class="text-xs leading-none text-gray-600 dark:text-gray-400">
          {@html highlightText(result.title || "-", currentSearchQuery)}
        </span>
      {:else if column.key === "company"}
        <span class="text-xs text-gray-600 dark:text-gray-400">
          {@html highlightText(result.companyName || "-", currentSearchQuery)}
        </span>
      {:else if column.key === "contact"}
        <span class="text-xs text-gray-600 dark:text-gray-400">
          {@html highlightText(result.email || "-", currentSearchQuery)}
        </span>
      {:else}
        <span class="text-xs text-gray-600 dark:text-gray-400">
          Column {colIndex + 1} Value
        </span>
      {/if}
    </td>
  {/each}
</tr>
